import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  onSnapshot,
  setDoc,
} from "firebase/firestore";
import { app } from "../firebase";
import "./Responses.css";

import { DashboardSidebar } from "./Header";
import BarChart from "./BarChart";
import LineChart from "./LineChart";

import BarChartIcon from "@mui/icons-material/BarChart";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";

const TABS = [
  {
    key: "score",
    label: "Score overview",
    icon: <BarChartIcon fontSize="small" />,
  },
];

function Responses() {
  const { id } = useParams();
  const [isActivate, setIsActivate] = useState(true);
  const [formInfo, setFormInfo] = useState({ questions: [], formName: "" });
  const [responses, setResponses] = useState({});
  const [responseCount, setResponseCount] = useState(0);
  const [activeTab, setActiveTab] = useState("score");
  const [copiedLink, setCopiedLink] = useState(false);

  useEffect(() => {
    if (!id) return;
    const db = getFirestore(app);

    // Fetch form metadata (formName, description, activation status)
    const fetchFormMeta = async () => {
      try {
        const formSnap = await getDoc(doc(db, "forms", id));
        if (formSnap.exists()) {
          const formData = formSnap.data();
          setFormInfo((prev) => ({ ...prev, ...formData }));
          if (formData.activate !== undefined) {
            setIsActivate(Boolean(formData.activate));
          }
        }
      } catch (err) {
        console.error("Error fetching form metadata:", err);
      }
    };

    fetchFormMeta();

    // Listen to real-time response submissions
    const responsesRef = collection(db, "responses", id, "responses");
    const unsubscribe = onSnapshot(
      responsesRef,
      (snapshot) => {
        const data = {};
        snapshot.forEach((d) => {
          data[d.id] = d.data();
        });
        setResponses(data);
        setResponseCount(snapshot.size);
      },
      (error) => {
        console.error("Error listening to responses:", error);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [id]);

  const handleSwitchChange = () => {
    const nextState = !isActivate;
    setIsActivate(nextState);

    const db = getFirestore(app);
    const formDocRef = doc(db, "forms", id);

    setDoc(formDocRef, { activate: nextState }, { merge: true })
      .then(() => {
        console.log(`Form activation updated to ${nextState}`);
      })
      .catch((error) => {
        console.error("Error updating activation status:", error);
      });
  };

  const handleCopyLink = () => {
    const origin = window.location.origin;
    const formUrl = `${origin}/form/${id}/instructions/userform`;
    navigator.clipboard.writeText(formUrl).then(() => {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2500);
    });
  };

  const convertToCSV = (data) => {
    if (!data || Object.keys(data).length === 0) {
      return "";
    }

    const csvRows = [];
    const headers = [
      "User ID",
      "Name",
      "Email",
      "Phone",
      "Is Anonymous",
      "Location",
      "Photo ID",
    ];
    const questionHeaders = [];

    const maxQuestions = Object.keys(data).reduce((max, docId) => {
      const d = data[docId];
      return Math.max(max, d.ques ? d.ques.length : 0);
    }, 0);

    for (let i = 1; i <= maxQuestions; i++) {
      questionHeaders.push(
        `Question ${i}`,
        `Submitted Answer ${i}`,
        `Answer Key ${i}`
      );
    }

    headers.push(...questionHeaders, "Total Points", "Percentage", "Timestamp");
    csvRows.push(headers.join(","));

    const escapeCsvField = (val) => {
      if (val === null || val === undefined) return '""';
      const str = String(val);
      return `"${str.replace(/"/g, '""')}"`;
    };

    for (const docId in data) {
      if (data.hasOwnProperty(docId)) {
        const docItem = data[docId];

        const userId =
          docItem["userId"] ||
          docItem["userName"] ||
          docItem["email"] ||
          "Anonymous";
        const userName =
          docItem["userName"] ||
          docItem["userId"] ||
          docItem["email"] ||
          "Anonymous";
        const email = docItem["userEmail"] || docItem["email"] || "N/A";
        const phone = docItem["userPhone"] || "N/A";
        const isAnonymous = docItem["isAnonymous"] ? "Yes" : "No";
        const location = docItem["location"]
          ? `${docItem["location"].latitude},${docItem["location"].longitude}`
          : "N/A";
        const photoId =
          docItem["photoId"] ||
          (docItem["photoDownloadURL"] &&
          !docItem["photoDownloadURL"].startsWith("data:")
            ? docItem["photoDownloadURL"]
            : "N/A");
        const totalPoints = docItem["totalPoints"] || 0;

        let timestamp = "N/A";
        if (docItem["timestamp"] && docItem["timestamp"].toDate) {
          timestamp = docItem["timestamp"].toDate().toISOString();
        } else if (
          docItem["timestamp"] &&
          !isNaN(new Date(docItem["timestamp"]).getTime())
        ) {
          timestamp = new Date(docItem["timestamp"]).toISOString();
        }

        const questions = docItem["ques"] || [];
        const userAnswers = docItem["responses"] || [];

        const totalPossiblePoints = questions.reduce(
          (sum, q) => sum + (Number(q.points) || 0),
          0
        );
        const percentage =
          totalPossiblePoints > 0
            ? ((totalPoints / totalPossiblePoints) * 100).toFixed(2)
            : "0.00";

        const questionData = [];
        for (let i = 0; i < maxQuestions; i++) {
          const questionText = questions[i] ? questions[i].questionText : "N/A";
          const userAnswer = userAnswers[i]
            ? Array.isArray(userAnswers[i].answer)
              ? userAnswers[i].answer.join("; ")
              : userAnswers[i].answer
            : "N/A";
          const answerKey = questions[i]
            ? Array.isArray(questions[i].answerKey)
              ? questions[i].answerKey.join("; ")
              : questions[i].answerKey
            : "N/A";
          questionData.push(
            escapeCsvField(questionText),
            escapeCsvField(userAnswer),
            escapeCsvField(answerKey)
          );
        }

        const row = [
          escapeCsvField(userId),
          escapeCsvField(userName),
          escapeCsvField(email),
          escapeCsvField(phone),
          escapeCsvField(isAnonymous),
          escapeCsvField(location),
          escapeCsvField(photoId),
          ...questionData,
          escapeCsvField(totalPoints),
          escapeCsvField(percentage),
          escapeCsvField(timestamp),
        ];

        csvRows.push(row.join(","));
      }
    }

    return csvRows.join("\n");
  };

  const handleDownload = () => {
    const csvData = convertToCSV(responses);
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.setAttribute("hidden", "");
    a.setAttribute("href", url);
    a.setAttribute(
      "download",
      `${(formInfo.formName || "responses").replace(/\s+/g, "_")}_export.csv`
    );
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Extract Question Texts and user responses for charts
  const usersList = Object.values(responses || {});
  let questionTexts = [];
  let queResponses = [];

  usersList.forEach((user) => {
    if (user && user.ques) {
      questionTexts = questionTexts.concat(
        user.ques.map((q) => q.questionText)
      );
    }
    if (user && user.responses) {
      queResponses = queResponses.concat(user.responses.map((r) => r));
    }
  });

  // Calculate Metrics
  let avgPercentage = 0;
  let highestPercentage = 0;
  let passCount = 0;
  let totalMaxPoints = 0;

  if (usersList.length > 0) {
    let percentageSum = 0;

    usersList.forEach((u) => {
      let maxPts = 0;
      if (u.ques && u.ques.length) {
        maxPts = u.ques.reduce(
          (tot, q) => tot + (Number(q.points) || 0),
          0
        );
      }
      if (maxPts > totalMaxPoints) totalMaxPoints = maxPts;

      const userPts = Number(u.totalPoints) || 0;
      let pct = 0;
      if (maxPts > 0) {
        pct = (userPts / maxPts) * 100;
      } else if (u.percentage !== undefined) {
        pct = Number(u.percentage);
      }

      percentageSum += pct;
      if (pct > highestPercentage) highestPercentage = pct;
      if (pct >= 50) passCount++;
    });

    avgPercentage = (percentageSum / usersList.length).toFixed(1);
    highestPercentage = highestPercentage.toFixed(1);
  }

  const passRate =
    usersList.length > 0
      ? Math.round((passCount / usersList.length) * 100)
      : 0;

  const formatTimestamp = (ts) => {
    if (!ts) return "—";
    try {
      const date = ts.toDate ? ts.toDate() : new Date(ts);
      if (isNaN(date.getTime())) return "—";
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "—";
    }
  };

  return (
    <div className="responses-detail-page">
      <DashboardSidebar activePage="Forms" />

      <div className="responses-detail-section">
        {/* Top Summary & Control Card */}
        <div className="responses-top-card">
          <div className="responses-top-header">
            <div className="responses-title-group">
              <h2>
                <span>
                  {responseCount} {responseCount === 1 ? "response" : "responses"}
                </span>
                <span className="responses-count-badge">Live</span>
              </h2>
              <p>
                {formInfo.formName
                  ? `Submissions for "${formInfo.formName}"`
                  : "Summary and real-time analytics for this form"}
              </p>
            </div>

            <div className="responses-controls-group">
              <label
                className="accepting-toggle"
                title="Toggle whether form accepts submissions"
              >
                <span className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={isActivate}
                    onChange={handleSwitchChange}
                  />
                  <span className="toggle-switch-track">
                    <span className="toggle-switch-thumb" />
                  </span>
                </span>
                <span className="accepting-toggle-label">
                  <span
                    className={`status-dot ${
                      isActivate ? "active" : "inactive"
                    }`}
                  />
                  {isActivate ? "Accepting responses" : "Not accepting responses"}
                </span>
              </label>

              <button
                type="button"
                className="responses-action-btn download-btn"
                onClick={handleDownload}
                disabled={responseCount === 0}
                title="Download all responses as CSV"
              >
                <FileDownloadOutlinedIcon fontSize="small" />
                <span>Download CSV</span>
              </button>

              <button
                type="button"
                className="responses-action-btn copy-link-btn"
                onClick={handleCopyLink}
                title="Copy public form link to share"
              >
                {copiedLink ? (
                  <>
                    <CheckRoundedIcon fontSize="small" style={{ color: "#137333" }} />
                    <span style={{ color: "#137333" }}>Copied!</span>
                  </>
                ) : (
                  <>
                    <ContentCopyOutlinedIcon fontSize="small" />
                    <span>Copy Form Link</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Responses Content */}
        {responseCount > 0 ? (
          <>
            {/* KPI Highlights Grid */}
            <div className="responses-metrics-grid">
              <div className="response-metric-card">
                <div className="metric-icon-wrap metric-icon-purple">
                  <PeopleAltOutlinedIcon fontSize="medium" />
                </div>
                <div className="metric-text-group">
                  <span className="metric-value">{responseCount}</span>
                  <span className="metric-label">Total Respondents</span>
                </div>
              </div>

              <div className="response-metric-card">
                <div className="metric-icon-wrap metric-icon-blue">
                  <TrendingUpOutlinedIcon fontSize="medium" />
                </div>
                <div className="metric-text-group">
                  <span className="metric-value">{avgPercentage}%</span>
                  <span className="metric-label">Average Score</span>
                </div>
              </div>

              <div className="response-metric-card">
                <div className="metric-icon-wrap metric-icon-green">
                  <EmojiEventsOutlinedIcon fontSize="medium" />
                </div>
                <div className="metric-text-group">
                  <span className="metric-value">{highestPercentage}%</span>
                  <span className="metric-label">Highest Score</span>
                </div>
              </div>

              <div className="response-metric-card">
                <div className="metric-icon-wrap metric-icon-amber">
                  <CheckCircleOutlineOutlinedIcon fontSize="medium" />
                </div>
                <div className="metric-text-group">
                  <span className="metric-value">{passRate}%</span>
                  <span className="metric-label">
                    Passing Rate ({passCount}/{responseCount})
                  </span>
                </div>
              </div>
            </div>

            {/* Score Distribution Chart Card */}
            <div className="score-card">
              <div className="score-card-header">
                <div className="score-card-title-wrap">
                  <h3>
                    <BarChartIcon />
                    Score distribution
                  </h3>
                  <p>Individual score percentage scored by each participant</p>
                </div>

                <div className="score-tabs">
                  {TABS.map((tab) => (
                    <button
                      type="button"
                      key={tab.key}
                      className={`score-tab ${
                        activeTab === tab.key ? "score-tab-active" : ""
                      }`}
                      onClick={() => setActiveTab(tab.key)}
                    >
                      {tab.icon}
                      <span>{tab.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="score-tab-panel">
                {activeTab === "score" && (
                  <BarChart response={responses} ques={questionTexts} />
                )}
                {activeTab === "line" && (
                  <LineChart response={queResponses} ques={questionTexts} />
                )}
              </div>
            </div>

            {/* Individual Responses Table Card */}
            <div className="individual-responses-card">
              <div className="individual-responses-header">
                <div>
                  <h3>Individual Submissions</h3>
                  <p>Detailed performance breakdown for each respondent</p>
                </div>
              </div>

              <div className="responses-table-wrapper">
                <table className="responses-table">
                  <thead>
                    <tr>
                      <th>Respondent</th>
                      <th>Score</th>
                      <th>Percentage</th>
                      <th>Submission Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersList.map((user, idx) => {
                      const email = user.email || user.userEmail || "";
                      const displayName = email || "Anonymous";
                      const initial = displayName.charAt(0).toUpperCase();

                      let maxPts = 0;
                      if (user.ques && user.ques.length) {
                        maxPts = user.ques.reduce(
                          (tot, q) => tot + (Number(q.points) || 0),
                          0
                        );
                      }
                      const userPoints = Number(user.totalPoints) || 0;
                      let pct = 0;
                      if (maxPts > 0) {
                        pct = Number(((userPoints / maxPts) * 100).toFixed(1));
                      } else if (user.percentage !== undefined) {
                        pct = Number(Number(user.percentage).toFixed(1));
                      }

                      let badgeClass = "score-badge-high";
                      if (pct < 50) badgeClass = "score-badge-low";
                      else if (pct < 80) badgeClass = "score-badge-mid";

                      return (
                        <tr key={idx}>
                          <td>
                            <div className="respondent-cell">
                              <div className="respondent-avatar">{initial}</div>
                              <div className="respondent-info">
                                <span className="respondent-name">
                                  {displayName}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <strong>{userPoints}</strong>
                            {maxPts > 0 ? ` / ${maxPts} pts` : " pts"}
                          </td>
                          <td>
                            <span className={`score-badge ${badgeClass}`}>
                              {pct}%
                            </span>
                          </td>
                          <td className="date-text">
                            {formatTimestamp(user.timestamp)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <div className="responses-detail-empty-card">
            <div className="empty-icon-circle">
              <AssignmentOutlinedIcon />
            </div>
            <h3>Waiting for responses</h3>
            <p>
              This form hasn't received any submissions yet. Share your form
              link with participants to start collecting and analyzing scores.
            </p>
            <button
              type="button"
              className="responses-action-btn download-btn"
              onClick={handleCopyLink}
            >
              <ContentCopyOutlinedIcon fontSize="small" />
              <span>{copiedLink ? "Link Copied!" : "Copy Form Link"}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Responses;